import { DataTable } from '@/components/admin/data-table';
import { ReportStatusBadge, StatusBadge } from '@/components/admin/status-badge';
import { type AdminReport, admin } from '@/lib/admin-api';
import { routes } from '@/lib/routes';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

export default function AdminReportsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [reports, setReports] = useState<AdminReport[]>([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [isLoading, setIsLoading] = useState(true);

  const statusFilter = searchParams.get('status');
  const reasonFilter = searchParams.get('reason');
  const page = Number(searchParams.get('page')) || 1;

  const fetchReports = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await admin.reports.list({
        status: statusFilter || undefined,
        reason: reasonFilter || undefined,
        page
      });
      setReports(result.reports);
      setPagination({
        page: result.pagination.page,
        totalPages: result.pagination.totalPages,
        total: result.pagination.total
      });
    } catch (err) {
      console.error('Failed to fetch reports:', err);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, reasonFilter, page]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleFilterChange = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.delete('page');
    setSearchParams(params);
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params);
  };

  const columns = [
    {
      key: 'reporter',
      header: t('admin.reports.reporter'),
      render: (report: AdminReport) => (
        <Link
          to={routes.admin.user(report.reporter.id)}
          className="text-ink hover:text-blue transition-colors"
        >
          {report.reporter.username}
        </Link>
      )
    },
    {
      key: 'reported',
      header: t('admin.reports.reported'),
      render: (report: AdminReport) => (
        <div>
          <Link
            to={routes.admin.user(report.reported.id)}
            className="text-ink hover:text-blue transition-colors"
          >
            {report.reported.username}
          </Link>
          <div className="mt-1">
            <StatusBadge status={report.reported.status} />
          </div>
        </div>
      )
    },
    {
      key: 'reason',
      header: t('admin.reports.reason'),
      render: (report: AdminReport) => (
        <div>
          <p className="font-medium">{report.reason.replace('_', ' ')}</p>
          {report.letter && (
            <p className="text-xs text-sepia/70">Letter: {report.letter.subject}</p>
          )}
        </div>
      )
    },
    {
      key: 'status',
      header: t('admin.reports.status'),
      render: (report: AdminReport) => <ReportStatusBadge status={report.status} />
    },
    {
      key: 'createdAt',
      header: t('admin.reports.createdAt'),
      render: (report: AdminReport) => (
        <span className="text-sepia/70 text-xs">
          {new Date(report.createdAt).toLocaleDateString()}
        </span>
      )
    },
    {
      key: 'actions',
      header: '',
      render: (report: AdminReport) => (
        <Link
          to={routes.admin.report(report.id)}
          className="text-blue hover:text-blue text-sm font-medium"
        >
          {t('admin.common.view')}
        </Link>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-ink">{t('admin.reports.title')}</h1>
        <p className="text-sepia/70 mt-1">{t('admin.reports.subtitle')}</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-warm p-4">
        <div className="flex gap-4">
          <select
            value={statusFilter || ''}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="px-3 py-2 border border-gray-warm rounded-lg focus:ring-2 focus:ring-blue focus:border-blue"
          >
            <option value="">{t('admin.reports.allStatuses')}</option>
            <option value="PENDING">Pending</option>
            <option value="REVIEWED">Reviewed</option>
            <option value="RESOLVED">Resolved</option>
            <option value="DISMISSED">Dismissed</option>
          </select>

          <select
            value={reasonFilter || ''}
            onChange={(e) => handleFilterChange('reason', e.target.value)}
            className="px-3 py-2 border border-gray-warm rounded-lg focus:ring-2 focus:ring-blue focus:border-blue"
          >
            <option value="">{t('admin.reports.allReasons')}</option>
            <option value="SPAM">Spam</option>
            <option value="HARASSMENT">Harassment</option>
            <option value="INAPPROPRIATE_CONTENT">Inappropriate Content</option>
            <option value="IMPERSONATION">Impersonation</option>
            <option value="OTHER">Other</option>
          </select>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={reports}
        keyExtractor={(report) => report.id}
        pagination={{
          page: pagination.page,
          totalPages: pagination.totalPages,
          total: pagination.total,
          onPageChange: handlePageChange
        }}
        emptyMessage={t('admin.reports.noReports')}
        isLoading={isLoading}
      />
    </div>
  );
}
